// @ts-strict-ignore
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import * as urltemplate from 'url-template';

import { AppConstants, LMConditionUtilService, LMUniqueIdService } from '@utils/index';
import { ListResource } from '@resources/list.resource';
import { LMEditorService } from './editors';
import { LMFormatterService } from './formatters';
import {
  LMBusyIndicatorService,
  LMCompoundFieldService,
  LMContextService,
  LMErrorUtilService,
  LMFormatUtilService,
  LMLoggerService,
  LMModalWrapperService,
  LMOverlayService,
  LMUrlUtilService,
} from '@services/index';
import {
  LMListEventService,
  LMListGridBuilderService,
  LMListGridPagingService,
  LMListGridUtilService,
  LMListSpecUtilService,
  LMListUtilService,
} from './services';
import { LMDataGridBaseComponent } from './lm-datagrid-base.component';
import { SohoBusyIndicatorEvent } from 'ids-enterprise-ng';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMLargeImageIsDialogService } from '@app/components';

@Component({
  selector: 'lm-list-grid',
  standalone: false,
  templateUrl: './lm-list-grid.component.html',
  styleUrls: [ 'lm-list-grid.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
  })
export class LMListGridComponent extends LMDataGridBaseComponent implements LMListContentInterface, OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @HostBinding('class.is-single-select') get isSingleSelectClass() {
    return this.lmListSpecUtilService.getSelectionMode(this.listSpec, this.inSelectListEditMode, this.allowMultiSelections) === 'single';
  }

  @HostBinding('class.has-button-row') get hasButtonRowClass() {
    return this.listSpec.busListSpec.displayFields[0]?.buttonRow;
  }

  @HostBinding('class.no-button-row') get noButtonRowClass() {
    return !this.listSpec.busListSpec.displayFields[0]?.buttonRow;
  }

  @HostBinding('class.hide-filter-types') hideFilterTypes = false;

  @Output() updateToolbarStateEvent = new EventEmitter<any>();

  // TODO: Remove after converting lm-lookup-click.function.ts into a service
  @Output() listBarCodeClicked = new EventEmitter<LMListBarCodeClickedEvent>();
  @Output() listLookupClicked = new EventEmitter<LMListLookupClickedEvent>();
  @Output() listFieldLookupClicked = new EventEmitter<LMListFieldLookupClickedEvent>();
  @Output() listFilterLookupClicked = new EventEmitter<LMListFilterLookupClickedEvent>();
  @Output() listFilterClearClicked = new EventEmitter<LMListFilterClearClickedEvent>();
  @Output() listOpenCompactForm = new EventEmitter<LMListOpenCompactFormEvent>();

  public data: any[];
  public dataGridId: string;
  public dataGridAutomationId: string;
  public pageSize: number;
  public hidePagerBar: boolean;
  public showPageSizeChooser: boolean;
  public enableFirstPage = false;
  public enablePreviousPage = false;
  public enableNextPage = false;
  public enableLastPage = false;
  public firstPageButtonTooltip: string;
  public previousPageButtonTooltip: string;
  public nextPageButtonTooltip: string;
  public lastPageButtonTooltip: string;
  public pagerId: string;
  public pagerAutomationId: string;
  public pagerIdAttributes: SohoAttributesArray;
  public uiLoadingIndicatorTimeout = 0;
  public testVersion1;
  public searchResultText: string;
  public sortOrder?: LMListSortOrder;
  public initialBuildComplete = false;

  private fFilterListCriteriaChanged = false;
  private implementsCancelablePaging = false;
  private cancelPagingNotifier$: Subject<string>;

  private fLoadInitialData = false;
  private fFromDataViewChanged = false;
  private fFilterCriteriaChanged = false;
  private fReportAsOfDateChanged = false;
  private listEventSubs: Subscription[] = [];
  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    protected lmUniqueIdService: LMUniqueIdService,
    private lmListGridPagingService: LMListGridPagingService,
    private lmListGridUtilService: LMListGridUtilService,

    protected lmConditionUtilService: LMConditionUtilService,
    protected lmContextService: LMContextService,
    protected lmEditorService: LMEditorService,
    protected lmFormatterService: LMFormatterService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmListEventService: LMListEventService,
    protected lmListGridBuilderService: LMListGridBuilderService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmLoggerService: LMLoggerService,
    protected lmThemeService: LMThemeService,
    protected lmListSpecUtilService: LMListSpecUtilService,
    protected lmListUtilService: LMListUtilService,
    protected lmCompoundFieldService: LMCompoundFieldService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected lmLargeImageIsDialogService: LMLargeImageIsDialogService,
    protected lmModalWrapperService: LMModalWrapperService,
    protected lmOverlayService: LMOverlayService
  ) {
    super(
      lmConditionUtilService,
      lmContextService,
      lmEditorService,
      lmFormatterService,
      lmFormatUtilService,
      lmListEventService,
      lmListGridBuilderService,
      lmLoggerService,
      lmThemeService,
      lmUrlUtilService,
      lmListSpecUtilService,
      lmListUtilService,
      lmCompoundFieldService,
      lmUniqueIdService,
      lmLargeImageIsDialogService,
      lmModalWrapperService,
      lmOverlayService
    );
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
    this.implementsCancelablePaging = this.lmContextService.isUiPagingRequestWaitTimeEnabled;
    this.testVersion1 = lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
    this.hideFilterTypes = lmContextService.isConfigFeatureEnabled('HideSearchFieldOptions');
  }

  ngOnInit() {
    this.clearEmptyMessage();
    this.validateInitialInputs();
    this.build();
    this.initialBuildComplete = true;
  }

  initialize() {
    super.initialize();

    this.dataGridId = this.listSpec?.busListSpec?.id + '_datagrid';
    this.dataGridAutomationId = 'lm-' + this.listSpec?.busListSpec?.automationId + '-datagrid';

    this.pagerId = `${this.listSpec.busListSpec.id}_pager`;
    this.pagerAutomationId = `lm-${this.listSpec?.busListSpec?.automationId}-pager`;
    this.pagerIdAttributes = [
      { name: 'id', value: this.pagerId },
      { name: 'data-automation-id', value: this.pagerAutomationId }
    ];

    this.listBarCodeClicked.subscribe(event => this.lmListEventService.listBarCodeClickedEvent(event));
    this.listLookupClicked.subscribe(event => this.lmListEventService.listLookupClickedEvent(event));
    this.listFieldLookupClicked.subscribe(event => this.lmListEventService.listFieldLookupClickedEvent(event));
    this.listFilterLookupClicked.subscribe(event => this.lmListEventService.listFilterLookupClickedEvent(event));
    this.listFilterClearClicked.subscribe(event => this.lmListEventService.listFilterClearClickedEvent(event));
    this.listOpenCompactForm.subscribe(event => this.lmListEventService.listOpenCompactFormEvent(event));
    this.hidePagerBar = this._hidePagerBar();
    this.pageSizes = this.lmListUtilService.getPageSizesArray(this.listSpec);
    this.pageSize = this.lmListUtilService.getPageSize(this.listSpec, this.listPersonalization);
    this.showPageSizeChooser = false; // this.lmListUtilService.showPageSizeChooser(this.listSpec);

    this.listEventSubs.push(this.lmListEventService.listGridContinuePagingEvent$.subscribe((pagingEvent: LMListGridPagingEvent) => {
      this.onContinuePaging(pagingEvent);
    }));

    this.listEventSubs.push(this.lmListEventService.reportAsOfDateSource$.subscribe((asOfDate: string) => {
      this.reportAsOfDate = asOfDate;
    }));

    // todo why not allow pagesize chooser and personalization on multi lists. seems more usable. Phillip 1/10/19
    // this.showPageSizeChooser = this.lmListUtilService.showPageSizeChooser(this.listSpec) && this.listSpec.listType !== 'multilist';
  }

  ngAfterViewInit() {
    // ---------------------------------------------------------------------------------
    // Related lists need to have the soho-datagrid built before fromDataViewChanged()
    // can be called. In most cases fromDataViewChanged can be called directly from the
    // '@Input() fromDataView` input setter. But for initial load of a dependent list
    // the datagrid isn't built before the fromDataView input is called. So we call
    // fromDataViewChanged in a timeout here so that change detection will fire.
    // ---------------------------------------------------------------------------------
    // if (this.listSpec.dependentList) {
    //   setTimeout(() => this.fromDataViewChanged());
    // }

    this.getSavedColumns();
    if (this.savedColumns && this.savedColumns.length > 0) {
      this.sohoDataGridComponent.restoreUserSettings({ columns: this.savedColumns });
    }

    // ---------------------------------------------------------------------------------
    // Non dependent lists can be loaded here, dependent list cannot
    // be loaded until there is fromDataView available
    // ---------------------------------------------------------------------------------
    if (!this.listSpec.dependentList) {
      this.fLoadInitialData = true;
    }
  }

  ngAfterViewChecked() {
    if (!this.sohoDataGridComponent || this.componentDestroyed) {
      return;
    }

    if (this.listSpec.dependentList && (this.fFromDataViewChanged || this.fFilterListCriteriaChanged)) {
      this.fFromDataViewChanged = false;
      this.fFilterListCriteriaChanged = false;
      // LMCLIENT-36782 - When periodview changes in cube view, the cube view will get reloaded.
      // But the listState.fromResourceId may be the same as fromDataView resourceId, causing initialPageUrl to be returned from liststate.
      // For eg: Say, we are looking at number of job applications processed by a recruiter. Changing
      // periodview from month to quarter will still have the same recruiter up on the top.
      // eg2: If the cube has a 'show total node', the first record is always going to be the total record, when period view changes.
      // eg3: If the user clicks on different rows, the fromDataView will change
      // In either cases,  the url has to be rebuilt using the new periodview and period view period.
      if (this.fromDataView?.cubeInfo) {
        this.listState.currentPageUrl = undefined;
      }
      setTimeout(() => this.initialPage());
      return;
    }

    const displayTypeChanged = !!this.listState.displayAsChanged;
    if (displayTypeChanged) {
      delete this.listState.displayAsChanged;

      if (this.filterCriteria) {
        this.fLoadInitialData = false;
        this.fFilterCriteriaChanged = true;
      } else {
        this.listState.currentPageUrl = undefined;
      }
    }

    if (this.fLoadInitialData) {
      // in case both load and filter criteria are set change these flags both to false
      this.fLoadInitialData = false;
      this.fFilterCriteriaChanged = false;
      this.fReportAsOfDateChanged = false;
      setTimeout(() => this.initialPage());
    }

    if (this.fFilterCriteriaChanged) {
      this.fFilterCriteriaChanged = false;

      // 'this.filterCriteria === null' means clear filter was clicked and we need to treat this
      // as an initial page so initial values are retrieved for the search form.
      // 'this.filterCriteria === []' (empty array) means do the filterPage so search form initial values
      // are not retrieved.
      if (this.lmListUtilService.isUseAsSearchTargetList(this.listSpec) && this.filterCriteria === null) {
        setTimeout(() => this.initialPage());
      } else {
        setTimeout(() => this.filterPage(this.filterCriteria, displayTypeChanged));
      }
    }

    if(this.fReportAsOfDateChanged) {
      this.fReportAsOfDateChanged = false;
      setTimeout(() => this.initialPage());
    }
  }

  ngOnDestroy() {
    this.setComponentDestroyed();
    this.cancelOutstandingPagingRequest();

    this.listEventSubs.forEach((sub: Subscription) => sub.unsubscribe());
  }

  public get listGridScrollableContentClasses(): string {
    if (this.lmListSpecUtilService.isSelectList(this.listSpec)
      && this.listSpec.busListSpec.searchForm === undefined
      && !this.listSpec.busListSpec.implementsTreeView) {
      return 'lm-lookup-listgrid';
    }
    return '';
  }

  public get showEmptyMessageOnList(): string {
    if ((!this.listSpec?.busListSpec?.noDataEmptyMessageInfo
        && this.listSpec?.listType === 'multilist')
        || (!this.listSpec?.busListSpec?.noDataEmptyMessageInfo?.icon
        && this.listSpec?.listType === 'multilist')) {
      return 'nofolder';
    }
    return '';
  }

  protected loadManualData() {
    this.initialPage();
  }

  protected fromDataViewChanged() {
    // See the actual processing of the changed fromDataView is in ngAfterViewChecked
    this.fFromDataViewChanged = true;
    this.changeDetectorRef.markForCheck();
  }

  protected filterCriteriaChanged() {
    // See the actual processing of the changed filterCriteriaChanged is in ngAfterViewChecked
    this.fFilterCriteriaChanged = true;
    this.changeDetectorRef.markForCheck();
  }

  protected reportAsOfDateChanged() {
    if(this.listSpec.userFolder) {
      this.fReportAsOfDateChanged = true;
      this.changeDetectorRef.markForCheck();
    }
  }

  public setSelectListFocus() {
    if (!this.lmContextService.useSingleSelectList() || !this.lmListSpecUtilService.isSelectList(this.listSpec)) {
      return;
    }

    const row = (this.allowMultiSelections) ? this.activatedRow() : this.getSelectedRow();
    if (row === -1) {
      return;
    }

    this.setActiveCell(row, 0);
  }

  public get datagridId(): string {
    if (!this.listSpec) {
      return null;
    }
    return this.listSpec.busListSpec.id + '_datagrid';
  }

  protected filterListCriteriaChanged() {
    if (this.hasFilterList && this.initialBuildComplete) {
      this.fFilterListCriteriaChanged = true;
      this.changeDetectorRef.markForCheck();
    }
  }

  public updateGridOptions() {
    // commenting out for now due to : issue in some fieldIds
    // LMCLIENT-40306 MISC AOC details missing on PO Header Add On Charge
    // To duplicate issue uncomment the code below and in uitest find a Product
    // list and config it to have Manufacturer.ManufacturerName in the display
    // field. This will cause the colon : to get into the automation id.
    // if (this.lmContextService.testVersion() >= 3) {
    //   this.gridOptions.attributes = [
    //     {
    //       name: 'id',
    //       value: this.listSpec?.busListSpec?.id
    //     }, {
    //       name: 'data-automation-id',
    //       value: `lm-${this.listSpec?.busListSpec?.id}`
    //     }
    //   ];
    // }
  }

  private static ServerLogVersionId = 'LMCLIENT UI ALERT: BUSY INDICATOR TIMEOUT';

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  onBusyIndicatorComplete(event?: SohoBusyIndicatorEvent) {
    if (this.isBusyPaging()) {
      const pagingInfo: LMCurrentPagingInfo = this.pagingSubscription['pagingInfo'];
      const errorStringArray = [
        `${LMListGridComponent.ServerLogVersionId}`,
        `id: ${this.dataGridId}`,
        `pageOp: ${pagingInfo.pageOp}`,
        `xhref: ${pagingInfo.url}`,
        `browserHref: ${window.location.href}`
      ];

      this.lmLoggerService.error(errorStringArray.join('\n'));
      this.lmLoggerService.logToServerConsole(errorStringArray.join(', '));
    }
  }

  private handleInitialPageCancel() {
    this.enableFirstPage = false;
    this.enablePreviousPage = false;
    this.enableNextPage = false;
    this.enableLastPage = false;
    this.sohoDataGridComponent.data = [];

    if (this.sohoDataGridComponent) {
      this.sohoDataGridComponent.removeSelected();
    }

    const message = '';
    const title = Soho.Locale.translate('NoDataAvailable');
    this.handleError(message, title);
    this.changeDetectorRef.markForCheck();
  }

  private handleCancelableRequest(): void {
      if (this.implementsCancelablePaging && this.cancelPagingNotifier$) {
        try {
          this.cancelPagingNotifier$.next(AppConstants.THREAD_INTERRUPTED_BY_WEBUI);
          this.cancelPagingNotifier$.complete();
          this.cancelPagingNotifier$ = null;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err) {
          // LMWEB-20425 when a paging call is made and handle unsaved changes causes this to error out.
        }
      }
  }

  private handleCancelableResponse(response: LMListPagingResponse): boolean {
    if (this.implementsCancelablePaging) {
      if (response.status === 'STARTED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'started', cancelablePagingNotifier: response.cancelablePagingNotifier });
        this.cancelPagingNotifier$ = response.cancelablePagingNotifier;
        return true;
      } else if (response.status === 'WAITING') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'waiting' });
        return true;
      } else if (response.status === 'CANCELED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'canceled' });
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        return true;
      } else {
        this.cancelPagingNotifier$ = null;
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'finished' });
        return false;
      }
    } else {
      return false;
    }
  }

  private initialPageWait(): Observable<boolean> {
    return this._initialPage();
  }

  private initialPage() {
    this._initialPage().subscribe();
  }

  public resetData(): void {
    this._initialPage().subscribe();
  }

  private _initialPage(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      // console.log('list-grid._initialPage: ' + this.listSpec.busListSpec.name);
      if (this.componentDestroyed) {
        observer.next(true);
        observer.complete();
        return;
      }

      this.handleCancelableRequest();

      if (this.listSpec.dependentList) {
        if (this.fromDataView === undefined) {
          observer.next(true);
          observer.complete();
          return; // safety
        }

        if (this.fromDataView === null) {
          // disabled flag should be set at this point! That makes disabling
          // the component here seem like we are doing disabled checking in 2 spots.
          // todo: ppatton Look into more consistent (and single) way of disabling list-grid, list-view, etc...
          this.disableComponent();

          /**
           * emitting the dataLoaded event with a null so the lm-list object
           * can responsd appropriately.
           * todo: ppatton again, why do this here?! shouldn't the lm-list already now it's been disabled?!
           */
          setTimeout(() => this.lmListEventService.dataLoadedEvent(null));
          observer.next(true);
          observer.complete();
          return;
        }
      }
      this.cancelOutstandingPagingRequest();
      // todo akila: deal with cube options - probably need to be in url somewhere set on the server
      if (this.lmContextService.useListFormOverlay()) {
        if (!this.isInOverlay) {
          this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
        }
      } else {
        this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
      }

      // rebuild the url if the exported filter criteria has changed
      let rebuildURL = (this.modelLpl !== undefined || (this.hasEFCChanged && this.listSpec.dependentList) || !!this.reportAsOfDate);
      if (!rebuildURL) {
        // rebuild url if not use as filter and no filter criteria exists. This is because
        // after a search, listState.currentUrl could have a fc on it that needs to be cleared
        if (this.lmListUtilService.isUseAsSearchTargetList(this.listSpec) &&
            !this.lmListUtilService.hasFilterCriteria(this.filterCriteria))
          rebuildURL = true;
      }
      const url = this.buildInitialPageUrl(rebuildURL);
      this.hasEFCChanged = false;

      const initialNbrRecs = this.lmListUtilService.getInitialNumberOfRecs(this.listSpec, this.listState, this.pageSize);

      const isManualLoad = this.isManualLoading();
      let postContent;

      if (this.modelLpl) {
        postContent = { requestType: 'useModelLPL', isList: true, modelLPL: this.modelLpl, userFolder: this.modelUserFolder};
      } else {
        postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
      }

      // -------------------------------------------------------------
      // request the first page of records and log how long it took
      // -------------------------------------------------------------
      this.lmLoggerService.time({ id: 'LMListGrid.initialPage() ' + this.instanceId });
      this.pagingSubscription = this.lmListGridPagingService
        .initialPageFiltered(url, this.getCombinedFilterCriteria(), initialNbrRecs, postContent, isManualLoad, this.listState.columnarOptions, this.listState.sortOrderCriteria)
        .subscribe((listPagingResponse: LMListPagingResponse) => {
          if (this.componentDestroyed) {
            observer.next(true);
            observer.complete();
            return;
          }

          if (this.handleCancelableResponse(listPagingResponse)) {
            if (listPagingResponse.status === 'CANCELED') {
              this.handleInitialPageCancel();
              observer.next(true);
              observer.complete();
            }
            return;
          }

          if (listPagingResponse.serverLog) {
            this.lmLoggerService.serverLog(listPagingResponse.serverLog);
          }

          this.lmLoggerService.timeEnd('LMListGrid.initialPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);

          if (this.lmContextService.useListFormOverlay()) {
            if (!this.isInOverlay) {
              this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
            }
          } else {
            this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          }
          this.lmLoggerService.time({ id: 'LMListGrid.initialPage() - processing response ' + this.instanceId });
          if (listPagingResponse.status === 'COMPLETED') {
            this.updateColumnVisibility(this.listSpec, listPagingResponse.dataViewSet);
            this.postInitialPage(listPagingResponse, 'init');

            if (listPagingResponse.columnFilterFields) {
              const sohoConditions = this.getSohoConditions(listPagingResponse.columnFilterFields);
              this.sohoDataGridComponent.setFilterConditions(sohoConditions);
              this.sohoDataGridComponent.applyFilter(sohoConditions);
              this.sohoDataGridComponent.toggleFilterRow();
            }

            // todo why is this here? The toolbar should have been updated via the onDataLoaded event.
            this.updateToolbarStateEvent.emit();
          } else {
            const useWrappedError = listPagingResponse.error?.message && !(listPagingResponse.error instanceof ProgressEvent);
            // For safety, error is not null, has message and not ProgressEvent which could be attributed to a network error
            const err = useWrappedError ? listPagingResponse.error : listPagingResponse;
            this.lmErrorUtilService.handleError(err.message, err.details, listPagingResponse.status, err.message);
            this.lmListEventService.buildToolbarEvent();
          }

          this.lmLoggerService.timeEnd('LMListGrid.initialPage() - processing response ' + this.instanceId);
          observer.next(listPagingResponse.status === 'COMPLETED');
          observer.complete();
        },
        (error: any) => {
          observer.next(false);
          observer.complete();

          if (this.lmListUtilService.showPageSizeChooser(this.listSpec)) {
            this.showPageSizeChooser = true;
            this.changeDetectorRef.markForCheck();
          }
          if (this.lmContextService.useListFormOverlay()) {
            if (!this.isInOverlay) {
              this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
            }
          } else {
            this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          }
          this.lmErrorUtilService.handleUnexpectedError(error);
        });

      this.setPagingSubscriptionPageOp(this.pagingSubscription, {
        pageOp: 'init',
        url
      });
    });

  }

  onBeforePaging(event: LMListGridPagingEvent) {
    this.lmListEventService.listGridBeforePagingEvent(event);
  }

  onContinuePaging(continuePagingEvent: LMListGridPagingEvent) {
    if (this.lmOverlayService.hasOpenOverlay && !this.isInOverlay && this.allowOverlayInteractions) {
      // Close overlay on a paging action on the opening list
      this.lmOverlayService.close();
    }
    switch (continuePagingEvent.pageOp) {
      case 'first':
        this.onFirstPage(continuePagingEvent, true);
        break;
      case 'last':
        this.onLastPage(continuePagingEvent, true);
        break;
      case 'next':
        this.onNextPage(continuePagingEvent, true);
        break;
      case 'previous':
        this.onPreviousPage(continuePagingEvent, true);
        break;
      case 'filter':
        this.onFilterPage(continuePagingEvent.sohoDataGridEvent, true);
        break;
      case 'sort':
        this.onSortPage(continuePagingEvent.sohoDataGridEvent, true);
        break;
      case 'pagesize':
        this.onPageSizeChange(continuePagingEvent, true);
        break;
    }
  }

  onPageSizeChange(event: LMListGridPagingEvent, continuePaging = false) {
    if (!continuePaging) {
      this.onBeforePaging(event);
      return;
    }

    this.pageSize = event.pageSize;
    this.listPersonalization.pageSize = event.pageSize;
    this.reloadPage();
  }

  /******************************************************************************
   * FIRST PAGE
   ******************************************************************************/
  onFirstPage(event: LMListGridPagingEvent, continuePaging = false): void {
    if (!continuePaging) {
      this.onBeforePaging(event);
      return;
    }

    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    let postContent;

    if (this.modelLpl) {
      postContent = { requestType: 'useModelLPL', isList: true, modelLPL: this.modelLpl, userFolder: this.modelUserFolder};
    } else {
      postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    }

    // request the first page of records and log how long it took
    this.lmLoggerService.time({ id: 'LMListGrid.firstPage() ' + this.instanceId });
    this.pagingSubscription = this.lmListGridPagingService.firstPage(this.dataViewSet, this.pageSize, postContent, this.listUrlInfo.cubeParams, this.selectRequestData, this.reportAsOfDate).subscribe(
      (listPagingResponse: LMListPagingResponse) => {
        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmLoggerService.timeEnd('LMListGrid.firstPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.processListPagingResponse(listPagingResponse);
          this.sohoDataGridComponent.scrollRowIntoView(0);
        } else {
          this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details,
            listPagingResponse.status, listPagingResponse.message);
        }
      },
      (error: any) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.lmErrorUtilService.handleUnexpectedError(error);
      });

    this.setPagingSubscriptionPageOp(this.pagingSubscription, {
      pageOp: 'first',
      url: this.dataViewSet.pagingUrls.firstPageUrl
    });
  }

  /******************************************************************************
   * LAST PAGE
   ******************************************************************************/
  onLastPage(event: LMListGridPagingEvent, continuePaging = false): void {
    if (!continuePaging) {
      this.onBeforePaging(event);
      return;
    }
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    let postContent;

    if (this.modelLpl) {
      postContent = { requestType: 'useModelLPL', isList: true, modelLPL: this.modelLpl, userFolder: this.modelUserFolder};
    } else {
      postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    }

    // request the last page of records and log how long it took
    this.lmLoggerService.time({ id: 'LMListGrid.lastPage() ' + this.instanceId });

    this.pagingSubscription = this.lmListGridPagingService.lastPage(this.dataViewSet, postContent, this.listUrlInfo.cubeParams, this.selectRequestData, this.reportAsOfDate).subscribe(
      (listPagingResponse: LMListPagingResponse) => {
        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmLoggerService.timeEnd('LMListGrid.lastPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.processListPagingResponse(listPagingResponse);
          this.sohoDataGridComponent.scrollRowIntoView(0);
        } else {
          this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details,
            listPagingResponse.status, listPagingResponse.message);
        }
      },
      (error: any) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.lmErrorUtilService.handleUnexpectedError(error);
      });

    this.setPagingSubscriptionPageOp(this.pagingSubscription, {
      pageOp: 'last',
      url: this.dataViewSet.pagingUrls.lastPageUrl
    });
  }

  /******************************************************************************
   * NEXT PAGE
   ******************************************************************************/
  onNextPage(event: LMListGridPagingEvent, continuePaging = false): void {
    if (!continuePaging) {
      this.onBeforePaging(event);
      return;
    }
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    let postContent;

    if (this.modelLpl) {
      postContent = { requestType: 'useModelLPL', isList: true, modelLPL: this.modelLpl, userFolder: this.modelUserFolder};
    } else {
      postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    }

    // request the next page of records and log how long it took
    this.lmLoggerService.time({ id: 'LMListGrid.nextPage() ' + this.instanceId });
    this.pagingSubscription = this.lmListGridPagingService.nextPage(this.dataViewSet, postContent, this.listUrlInfo.cubeParams, this.selectRequestData, this.reportAsOfDate).subscribe(
      (listPagingResponse: LMListPagingResponse) => {
        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmLoggerService.timeEnd('LMListGrid.nextPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.processListPagingResponse(listPagingResponse);
          this.sohoDataGridComponent.scrollRowIntoView(0);
        } else {
          this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details,
            listPagingResponse.status, listPagingResponse.message);
        }
      },
      (error: any) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.lmErrorUtilService.handleUnexpectedError(error);
      });

    this.setPagingSubscriptionPageOp(this.pagingSubscription, {
      pageOp: 'next',
      url: this.dataViewSet.pagingUrls.nextPageUrl
    });
  }

  /******************************************************************************
   * PREVIOUS PAGE
   ******************************************************************************/
  onPreviousPage(event: LMListGridPagingEvent, continuePaging = false): void {
    if (!continuePaging) {
      this.onBeforePaging(event);
      return;
    }
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    let postContent;

    if (this.modelLpl) {
      postContent = { requestType: 'useModelLPL', isList: true, modelLPL: this.modelLpl, userFolder: this.modelUserFolder};
    } else {
      postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    }

    this.lmLoggerService.time({ id: 'LMListGrid.previousPage() ' + this.instanceId });
    this.pagingSubscription = this.lmListGridPagingService.previousPage(this.dataViewSet, postContent, this.listUrlInfo.cubeParams, this.selectRequestData, this.reportAsOfDate).subscribe(
      (listPagingResponse: LMListPagingResponse) => {
        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmLoggerService.timeEnd('LMListGrid.previousPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.processListPagingResponse(listPagingResponse);
          this.sohoDataGridComponent.scrollRowIntoView(0);
        } else {
          this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details,
            listPagingResponse.status, listPagingResponse.message);
        }
      },
      (error: any) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.lmErrorUtilService.handleUnexpectedError(error);
      });

    this.setPagingSubscriptionPageOp(this.pagingSubscription, {
      pageOp: 'previous',
      url: this.dataViewSet.pagingUrls.previousPageUrl
    });
  }

  /******************************************************************************
   * SORT PAGE
   ******************************************************************************/
  onSortPage(event: SohoDataGridSortedEvent, continuePaging = false): void {
    if (!continuePaging) {
      const beforePagingEvent: LMListGridPagingEvent = { pageOp: 'sort', sohoDataGridEvent: event };
      this.onBeforePaging(beforePagingEvent);
      return;
    }

    const sortColumn = this.sohoDataGridComponent.columnById(event.sortId)[ 0 ];
    const sortAscending = event.sortAsc;
    let sortOrderName;

    if (this.lmListSpecUtilService.isColumnarView(this.listSpec)) {
      sortOrderName = sortColumn.displayField.fieldId;

      if (!this.listState.columnarOptions) {
        this.listState.columnarOptions = {};
      }

      this.listState.columnarOptions.selectedDimension = sortOrderName;
      this.listState.columnarOptions.selectedOrderBy = sortOrderName;
      this.listState.columnarOptions.isAscending = sortAscending;
    } else {
      sortOrderName = sortColumn.displayField.sortOrderName;
    }

    const sortOrder = { sortOrderName, isAscending: sortAscending };
    this.sortPage(sortOrder, continuePaging);
  }

  sortPage(sortOrder: LMListSortOrder, continuePaging = false): void {
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    const url = this.buildInitialPageUrl(true);

    let postContent;

    if (this.modelLpl) {
      postContent = { requestType: 'useModelLPL', isList: true, modelLPL: this.modelLpl, userFolder: this.modelUserFolder};
    } else {
      postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    }

    this.sortOrder = sortOrder;
    this.listState.sortOrderCriteria = sortOrder;

    // clicking the sort on a column header shouldn't return any data if the search button hasn't
    // been clicked when on a use as search case
    const pageSize = this.lmListUtilService.getInitialNumberOfRecs(this.listSpec, this.listState, this.pageSize);

    // request the sorted page of records and log how long it took
    this.lmLoggerService.time({ id: 'LMListGrid.sortPage() ' + this.instanceId });
    this.pagingSubscription = this.lmListGridPagingService.sortPage(url, this.getCombinedFilterCriteria(), sortOrder, pageSize, postContent, this.listUrlInfo.cubeParams, this.selectRequestData).subscribe(
      (listPagingResponse: LMListPagingResponse) => {
        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmLoggerService.timeEnd('LMListGrid.sortPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        this.lmLoggerService.time({ id: 'LMListGrid.sortPage() - processing response ' + this.instanceId });
        if (listPagingResponse.status === 'COMPLETED') {
            // ----------------------------------------------------------------------------------
            // initial, filter and sort page should call postInitialPage
            // first, last, next, prev, and refresh/reload should call processListPagingResponse.
            // ----------------------------------------------------------------------------------
          this.listState.multiSelectedValues = this.multiSelectedValues;
          listPagingResponse.resetSelection = true;
          this.postInitialPage(listPagingResponse, 'sort');
        } else {
          this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details,
            listPagingResponse.status, listPagingResponse.message);
        }
        this.lmLoggerService.timeEnd('LMListGrid.sortPage() - processing response ' + this.instanceId);
      },
      (error: any) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.lmErrorUtilService.handleUnexpectedError(error);
      });

    this.setPagingSubscriptionPageOp(this.pagingSubscription, {
      pageOp: 'sort',
      url
    });
  }

  // leaving this here in case we need to do filterCriteria comparisons in the future
  // private compareFilterCriteria(event: SohoDataGridFilteredEvent): boolean {
  //   if (this.listState.filterCriteria) {
  //     let incomingFilterConditions = this.getFilterCriteria(event.conditions);
  //     if (incomingFilterConditions.length !== this.listState.filterCriteria.length) {
  //       return false;
  //     }
  //
  //     let matchedFilters = true;
  //     for (let index = 0; incomingFilterConditions.length > index; index++) {
  //       let incCondition = incomingFilterConditions[ index ];
  //       let storedCondition = this.listState.filterCriteria.find(x => x.fieldId === incCondition.fieldId);
  //       if (storedCondition) {
  //         matchedFilters = (incCondition.operator === storedCondition.operator && incCondition.value === storedCondition.value);
  //         if (!matchedFilters) {
  //           return false;
  //         }
  //       } else {
  //         return false;
  //       }
  //     }
  //     return matchedFilters;
  //   }
  //   return false;
  // }

  /******************************************************************************
   * FILTER PAGE
   ******************************************************************************/
  onFilterPage(event: SohoDataGridFilteredEvent, continueFiltering = false): void {
    if (this.disabled) {
      // -------------------------------------------------------------
      // this is here until soho grid.js has a disabled state that
      // would restrict the filter bar field entry from messaging
      // this function.
      // -------------------------------------------------------------
      return;
    }

    // LMCLIENT-38371: Adding --> && this.columnfilterOperators.size === 0 will make the modifier sticky
    // if it is changed before setting the value of listState.filterCriteria
    if (event.op === 'clear' || ((!event.conditions || event.conditions.length === 0) && this.columnfilterOperators.size === 0)) {
      if (!this.listState.filterCriteria || this.listState.filterCriteria.length === 0) {
        // --------------------------------------------------------------------------
        // getting a 'filtered' event when closing the filter row. On the close
        // filter row event I set a flag and if we are asked to close a filter
        // we should only clear the filter if a filter has already ben executed
        // --------------------------------------------------------------------------
        return;
      }
    }

    if (!this.lmListUtilService.isAutoFilterEnabled() && event?.trigger === 'selected') {
      return;
    }

    if (!continueFiltering) {
      const beforePagingEvent: LMListGridPagingEvent = { pageOp: 'filter', sohoDataGridEvent: event };
      this.onBeforePaging(beforePagingEvent);
      return;
    }
    // --------------------------------------------------------------------------
    // setting filterCriteria input here will causes a filterCriteriaChanged()
    // message which will cause filterPage() to be called during the
    // ngAfterViewChecked lifecycle event.
    // --------------------------------------------------------------------------
    const pendingFilterCriteria = this.getFilterCriteria(event.conditions);

    if (this.lmListUtilService.areTheSameJson(pendingFilterCriteria, this.filterCriteria)) {
      return;
    }

    if (this.validateFilterCriteria(pendingFilterCriteria)) {
      this.lmListEventService.updateFilterCriteriaEvent(pendingFilterCriteria);
    }
  }

  private filterPage(filterCriteria: LMFilterFieldCriteria[], displayTypeChanged = false) {
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmLoggerService.log([ this, this.instanceId, 'datagrid.source - filtered called' ]);

    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    // TODO: this code can be removed when the select all checkbox is not shown in selection mode
    if (this.allowMaintainingMultiSelections && !this.inSelectListEditMode) {
      this.doNotClearSelectionTags = true;
    }
    this.lmListGridUtilService.unSelectAllRows(this.sohoDataGridComponent);

    const url = this.buildInitialPageUrl(true);

    let postContent;

    if (this.modelLpl) {
      postContent = { requestType: 'useModelLPL', isList: true, modelLPL: this.modelLpl, userFolder: this.modelUserFolder};
    } else {
      postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    }

    // request the first page of records and log how long it took
    this.lmLoggerService.time({ id: 'LMListGrid.filterPage() ' + this.instanceId });
    this.pagingSubscription = this.pagingSubscription = this.lmListGridPagingService
      .filterPage(url, this.getCombinedFilterCriteria(), this.pageSize, postContent, this.sortOrder, this.listUrlInfo.cubeParams, this.listState.columnarOptions)
      .subscribe((listPagingResponse: LMListPagingResponse) => {
        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmLoggerService.timeEnd('LMListGrid.filterPage() ' + this.instanceId, [ this, this.dataViewSet, 'pageSize: ' + this.pageSize, 'filterCriteria:', this.m_filterCriteria ]);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        this.lmLoggerService.time({ id: 'LMListGrid.filterPage() - processing response ' + this.instanceId });
        if (listPagingResponse.status === 'COMPLETED') {
          // ----------------------------------------------------------------------------------
          // initial, filter and sort page should call postInitialPage
          // first, last, next, prev, and refresh/reload should call processListPagingResponse.
          // ----------------------------------------------------------------------------------
          this.listState.filterCriteria = filterCriteria;
          this.listState.sortOrderCriteria = this.sortOrder;
          this.listState.multiSelectedValues = this.multiSelectedValues;
          if (displayTypeChanged) {
            this.updateColumnVisibility(this.listSpec, listPagingResponse.dataViewSet);
            this.updateColumnLabels(this.listSpec, listPagingResponse.dataViewSet);
          }
          this.updateColumnSort(this.listSpec, listPagingResponse.dataViewSet, filterCriteria);
          this.postInitialPage(listPagingResponse, 'filter');

          if (listPagingResponse.dataViewSet && listPagingResponse.dataViewSet.data.length === 0) {
            this.searchResultText = Soho.Locale.translate('NoSearchResultsFound');
          } else {
            const searchTags = this.lmListUtilService.buildSearchTags(this.listState.filterCriteria);
            const searchCriteria = searchTags.reduce((text, criteria) => `${text}, ${criteria.label} : ${criteria.tag}`, '');

            this.searchResultText = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('SearchResultsFound'), searchCriteria);
          }
        } else {
          this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details,
            listPagingResponse.status, listPagingResponse.message);
          this.searchResultText = Soho.Locale.translate('UnexpectedErrorOccured');
        }
        this.lmLoggerService.timeEnd('LMListGrid.filterPage() - processing response ' + this.instanceId);
      },
      (error: any) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.lmErrorUtilService.handleUnexpectedError(error);
      });

    this.setPagingSubscriptionPageOp(this.pagingSubscription, {
      pageOp: 'filter',
      url
    });
  }

  /*****************************************************************************
   * RELOAD PAGE
   *****************************************************************************/
  private reloadPage(): void {
    this._reloadPage().subscribe();
  }

  private reloadPageAndWait(): Observable<boolean> {
    return this._reloadPage();
  }

  private _reloadPage(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (this.componentDestroyed) {
        observer.next(true);
        observer.complete();
        return;
      }

      // if (!this.lmListUtilService.isUseAsSearchTargetList(this.listSpec) &&
      //     (this.filterCriteria === undefined || this.filterCriteria === null || this.filterCriteria?.length === 0)) {
      //   // If list has search form and no use as filter, only update the data if search has been clicked.
      //   // If no filterCriteria is set, then don't retrieve data
      //   observer.next(true);
      //   observer.complete();
      //   return;
      // }

      this.handleCancelableRequest();
      this.cancelOutstandingPagingRequest();

      this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

      let _reportAsOfDate;

      if(this.listSpec.userFolder && this.reportAsOfDate) {
        _reportAsOfDate = this.reportAsOfDate;
      }

      const selectedRowsResourceIds = this.lmListUtilService.convertRowIndexesToResourceIds(this.getSelectedRows(), this.getDataViewSet());

      // do not unselect all rows when maintaining selected values and in selection mode
      const unselectAllRows = (this.allowMaintainingMultiSelections && !this.inSelectListEditMode) ? false : true;

      // Helper list is the exception since a transaction has run to add
      // the selected items to the list and should not be selected
      const selectedRow = this.lmListSpecUtilService.isHelperList(this.listSpec)
        ? -1
        : this.lmListGridUtilService.getSelectedIndex(this.sohoDataGridComponent, unselectAllRows);
      const activatedRow = this.lmListGridUtilService.getActivatedRow(this.sohoDataGridComponent, true);

      let postContent;

      if (this.modelLpl) {
        postContent = { requestType: 'useModelLPL', isList: true, modelLPL: this.modelLpl, userFolder: this.modelUserFolder};
      } else {
        postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
      }

      const pageSize = this.lmListUtilService.getInitialNumberOfRecs(this.listSpec, this.listState, this.pageSize);

      // request the first page of records and log how long it took
      this.lmLoggerService.time({ id: 'LMListGrid.reloadPage() ' + this.instanceId });
      this.pagingSubscription = this.lmListGridPagingService.reloadPage(this.dataViewSet, pageSize, postContent, this.listUrlInfo.cubeParams, null, this.selectRequestData, this.reportAsOfDate).subscribe(
        (listPagingResponse: LMListPagingResponse) => {
          if (this.handleCancelableResponse(listPagingResponse)) {
            observer.next(true);
            observer.complete();
            return;
          }

          if (listPagingResponse.serverLog) {
            this.lmLoggerService.serverLog(listPagingResponse.serverLog);
          }

          this.lmLoggerService.timeEnd('LMListGrid.reloadPage() ' + this.instanceId, [ this, this.dataViewSet, 'newPageSize: ' + this.pageSize ]);
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

          if (listPagingResponse.status === 'COMPLETED') {
            this.updateColumnVisibility(this.listSpec, listPagingResponse.dataViewSet);
            this.updateColumnLabelsOrSortIndicator(this.listSpec, listPagingResponse.dataViewSet, this.getCombinedFilterCriteria());
            this.processListPagingResponse(listPagingResponse, false, selectedRow, activatedRow, selectedRowsResourceIds);
          } else {
            this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details,
              listPagingResponse.status, listPagingResponse.message);
          }

          observer.next(listPagingResponse.status === 'COMPLETED');
          observer.complete();
        },
        (error: any) => {
          observer.next(false);
          observer.complete();
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          this.lmErrorUtilService.handleUnexpectedError(error);
        });

      this.setPagingSubscriptionPageOp(this.pagingSubscription, {
        pageOp: 'reload',
        url:    ''
      });
    });
  }

  public clearData() {
    this.sohoDataGridComponent.updateDataset([]);
  }

  public refresh(): Observable<boolean> {
    // if the list is asked to refresh from another component
    // then do an initial page because the from dataview may have changed
    // a field that impacts column visibility!  The page state should keep
    // the list on the same page.

    if (this.isBusyPaging()) {
      return of (true);
    }

    this.clearDirty();
    // console.log('list-grid refresh: ' + this.listSpec.busListSpec.name);
    // return this.initialPageWait();
    return this.reloadPageAndWait();
  }

  private buildInitialPageUrl(forceUrlRebuild = false): string {

    if (this.listSpec.dependentList) {
      if (this.listState
        && this.listState.currentPageUrl
        && !forceUrlRebuild
        && !this.dependencyChanged()
        && this.lmUrlUtilService.allCsksMatchContext(this.listState.currentPageUrl)
      ) {
        // ------------------------------------------------------------------
        // if the fromDataView hasn't changed and there is a currentPageUrl
        // then load that to keep our position in the dependent list.
        // ------------------------------------------------------------------
        return this.listState.currentPageUrl;
      }

      // ------------------------------------------------------------------
      // else resolve template against the fromDataView's resourceId and
      // it's filterListFilterCriteria if this list has a FilterList
      // dependency
      // ------------------------------------------------------------------
      const compiledUrlTemplate = urltemplate.parse(this.listUrlInfo.initialDataUrl);
      let compiledUrl = compiledUrlTemplate.expand({ resourceId: this.fromDataView.resourceId });
      let delimiter: string = compiledUrl.indexOf('?') === -1 ? '?' : '&';

      if (this.fromDataView.cubeInfo !== undefined) {
        compiledUrl = compiledUrl + delimiter + this.lmUrlUtilService.objectToCubeQueryString(this.fromDataView.cubeInfo);
        delimiter = '&';
        if (this.fromDataView.cubeInfo.extraInfo) {
          compiledUrl = compiledUrl + delimiter + this.lmUrlUtilService.objectToCubeQueryString(this.fromDataView.cubeInfo.extraInfo);
        }
        if (this.fromDataView.cubeInfo.extraInfo2) {
          this.lmLoggerService.log([ this, this.instanceId, 'LMListGridComponent:buildInitialPageUrl:pvperiods - ', this.fromDataView.cubeInfo.extraInfo2.pvPeriods.length ]);
        }
      }

      if (this.hasFilterList && this.filterListFilterCriteria) {
        // ------------------------------------------------------------------
        // apply any filterListFilterCriteria to the initial load.
        // ------------------------------------------------------------------    // set filter criteria on the url params
        const filterParamString = this.lmUrlUtilService.filterFieldCriteriaToUrlParams(this.filterListFilterCriteria, 'flc');
        if (filterParamString) {
          compiledUrl += delimiter + filterParamString;
        }
      }

      // TODO: Is filterParam/filterString either/or that delimiter need not be updated?
      // Isn't this done in lm-list-grid-paging.service.ts? Why are we building it again?
      // const filterCriteria: Array<LMFilterFieldCriteria> = this.lmListUtilService.ignoreStickyOperator(this.filterCriteria);
      // if (filterCriteria) {
      //   const filterCriteriaString = this.lmUrlUtilService.filterFieldCriteriaToUrlParams(filterCriteria, 'fc');
      //   compiledUrl += delimiter + filterCriteriaString;
      // }

      return compiledUrl;

    } else if (this.listUrlInfo.cubeParams) {
      // Cube Drill requires cube option parameters.
      return this.listUrlInfo.initialDataUrl + '&' + this.lmUrlUtilService.objectToCubeQueryString(this.listUrlInfo.cubeParams);
    } else if (this.listUrlInfo.urlParams && this.listUrlInfo.urlParams['aggDrill']) {
      return this.listUrlInfo.initialDataUrl;
    }

    if (this.listState.currentPageUrl
      && this.lmUrlUtilService.allCsksMatchContext(this.listState.currentPageUrl)
      && this.lmUrlUtilService.hasSameParamValue(this.listState.currentPageUrl, this.listUrlInfo.initialDataUrl, 'filter')
      && !forceUrlRebuild
    ) {
      return this.listState.currentPageUrl;
    }

    let url = this.listUrlInfo.initialDataUrl;
    if (this.selectRequestData?.onSearchForm) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'searchform', 'true');
    }

    if(this.reportAsOfDate) {
      url= this.lmUrlUtilService.addParamToUrl(url, 'reportAsOfDate', this.reportAsOfDate);
    }

    return url;
  }

  private dependencyChanged() {
    if (!this.listSpec.dependentList || !this.listState) {
      // --------------------------------------------------------------
      // Must be a dependent list and have listState to check against
      // for a dependency to have changed. If not return false.
      // --------------------------------------------------------------
      return false;
    }

    // --------------------------------------------------------------
    // If the from dataView resourceId change then the dependency
    // has changed. return true.
    // --------------------------------------------------------------
    if (this.fromDataView.cubeInfo) {
      return this.fromDataView.dataViewResId !== this.listState.fromResourceId;
    } else if (this.fromDataView.resourceId !== this.listState.fromResourceId) {
      return true;
    }

    if (this.hasFilterList && !this.lmListUtilService.areTheSame(this.filterListFilterCriteria, this.listState.filterListFilterCriteria)) {
      // --------------------------------------------------------------
      // If this list has a filterList then check to see if the
      // filterListFilterCriteria has changed. If so then return true.
      // --------------------------------------------------------------
      return true;
    }

    return !this.lmListUtilService.areTheSame(this.filterCriteria, this.listState.filterCriteria);
  }

  private processListPagingResponse(
    listPagingResponse: LMListPagingResponse,
    postInitialPage = false,
    selectRow = -1,
    activatedRow = 0,
    selectedRowsResourceIds = []
  ) {
    /**
     * Add specFields to dataViewSet
     */
    this.lmListUtilService.setSpecFields(listPagingResponse.dataViewSet);

    /**
     * Set the member dataViewSet variable
     */
    this.dataViewSet = listPagingResponse.dataViewSet;

    // Set Saved Search dataViewSet
    this.savedSearches = listPagingResponse.savedSearchDataViewSet;

    /**
     * Store the current paging url into list state
     */
    this.listState.currentPageUrl = this.dataViewSet.pagingUrls.reloadPageUrl;
    if (this.fromDataView && this.fromDataView.cubeInfo) {
      this.lmUrlUtilService.copyCubeParamsForDetailSection(this.listUrlInfo, this.fromDataView);
      this.listState.currentPageUrl += '&' + this.lmUrlUtilService.objectToCubeQueryString(this.listUrlInfo.cubeParams);
    }

    if (listPagingResponse.status === AppConstants.STATUS_COMPLETED) {
      performance.mark('LoadDataIntoDataGrid started');
      this.loadDataIntoDataGrid();
      performance.mark('LoadDataIntoDataGrid finished');
      performance.measure('LoadDataIntoDataGrid', 'LoadDataIntoDataGrid started', 'LoadDataIntoDataGrid finished');
      performance.clearMarks();
      performance.clearMeasures('LoadDataIntoDataGrid');
      if (postInitialPage) {
        // WORAROUND for now.
        // todo place section below in it's own function an call directly from
        // first/last/prev/next/etc.. functions directly
        return;
      }

      // if single select use select vs activate row
      const selectionMode = this.lmListSpecUtilService.getSelectionMode(this.listSpec, this.inSelectListEditMode, this.allowMultiSelections);
      const isSingleSelect = selectionMode === 'single';
      if (isSingleSelect) {
        selectRow = selectRow > -1 ? selectRow : 0;
        activatedRow = -1;
      } else if (selectionMode === 'mixed') {
        activatedRow = activatedRow > -1 ? activatedRow : 0;
      }

      // --------------------------------------------------------------
      // need in a timeout to make sure the datagrid is built and
      // loaded before attempting to call functions on it.
      // --------------------------------------------------------------
      setTimeout(() => {
        if (this.componentDestroyed) {
          return;
        }

        this.selectionInProgress = true;
        this.clearDirty();

        let rowActivated = false;
        if (activatedRow !== undefined && activatedRow > -1) {
          if (activatedRow >= this.dataViewSet.data.length) {
            activatedRow = this.dataViewSet.data.length - 1;
          }
          // https://jira.lawson.com/browse/LMCLIENT-25037
          if (activatedRow > -1) {
            this.activateRow(activatedRow);
            rowActivated = true;
          }
        }

        if (selectRow > -1) {
          if (!isSingleSelect && this.lmContextService.enableMaintainingSelectedRows() && selectedRowsResourceIds?.length > 0) {
            const rowsToBeSelected = this.lmListUtilService.convertResourceIdsToRowIndexes(selectedRowsResourceIds, this.dataViewSet);
            if (rowsToBeSelected?.length > 0) {
              this.sohoDataGridComponent.selectRows(rowsToBeSelected);
            } else {
                // need to fire an event to update the toolbar when no row is selected
              this.lmListEventService.listRowSelectedEvent(<LMListRowSelectedEvent>{ event: { }, rows: [] });
            }
          } else {
            if (selectRow >= this.dataViewSet.data.length) {
              selectRow = this.dataViewSet.data.length - 1;
            }
            this.lmListGridUtilService.selectRow(this.sohoDataGridComponent, selectRow);
          }
        } else {
           // need to fire an event to update the toolbar when no row is selected
          this.lmListEventService.listRowSelectedEvent(<LMListRowSelectedEvent>{ event: { }, rows: [] });
        }
        this.selectionInProgress = false;

        if (!rowActivated) {
          // need to fire an event to update related content like detail panels
          const listRowActivatedEvent: LMListRowActivatedEvent = {
            row: isSingleSelect ? selectRow : activatedRow,
            item: null,
            fromDataView: this.fromDataView,
            sourceIsPagingEvent: this.selectionInProgress
          };
          this.lmListEventService.listRowActivatedEvent(listRowActivatedEvent);
        }

        this.lmListEventService.listGridAfterPagingEvent(listPagingResponse);
      });
    } else {
      // todo handle other landmark return status
    }
  }

  private loadDataIntoDataGrid() {
    if (this.disabled) {
      // safety - in case function is called when disabled
      this.disableComponent();
      return;
    }
    this.enableFirstPage = this.dataViewSet.pagingInfo.hasPrevious && !this.dataViewSet.pagingInfo.firstDisabled && this.dataViewSet.data.length > 0;
    this.enablePreviousPage = this.dataViewSet.pagingInfo.hasPrevious && !this.dataViewSet.pagingInfo.previousDisabled && this.dataViewSet.data.length > 0;
    this.enableNextPage = this.dataViewSet.pagingInfo.hasNext && !this.dataViewSet.pagingInfo.nextDisabled && this.dataViewSet.data.length > 0;
    this.enableLastPage = this.dataViewSet.pagingInfo.hasNext && !this.dataViewSet.pagingInfo.lastDisabled && this.dataViewSet.data.length > 0;

    /**
     * If used as search and no recs then don't show the pageSizeChooser.
     * todo maybe make a ticket for tim to disable it.
     */
    if (this.lmListUtilService.isUseAsSearchTargetList(this.listSpec)) {
      this.showPageSizeChooser = this.dataViewSet.data.length > 0;
    } else {
      this.showPageSizeChooser = this.lmListUtilService.showPageSizeChooser(this.listSpec);
    }

    this.firstPageButtonTooltip = !this.enableFirstPage && this.dataViewSet.pagingInfo.firstDisabledReason ?
      this.dataViewSet.pagingInfo.firstDisabledReason : Soho.Locale.translate('FirstPage');

    this.previousPageButtonTooltip = !this.enablePreviousPage && this.dataViewSet.pagingInfo.previousDisabledReason ?
      this.dataViewSet.pagingInfo.previousDisabledReason : Soho.Locale.translate('PreviousPage');

    this.nextPageButtonTooltip = !this.enableNextPage && this.dataViewSet.pagingInfo.nextDisabledReason ?
      this.dataViewSet.pagingInfo.nextDisabledReason : Soho.Locale.translate('NextPage');

    this.lastPageButtonTooltip = !this.enableLastPage && this.dataViewSet.pagingInfo.lastDisabledReason ?
      this.dataViewSet.pagingInfo.lastDisabledReason : Soho.Locale.translate('LastPage');

    this.disableSelectionListener = true; // ignore unselectall event coming from datagrid.loadData

    this.lmLoggerService.time({ id: 'LMListGrid.loadDataIntoDataGrid() - setting data ' + this.instanceId });
    this.sohoDataGridComponent.data = this.dataViewSet.data;
    this.lmLoggerService.timeEnd('LMListGrid.loadDataIntoDataGrid() - setting data ' + this.instanceId);

    this.disableSelectionListener = false;

    this.setEmptyMessage();

    this.hidePagerBar = this._hidePagerBar();
    this.changeDetectorRef.markForCheck();
  }

  public showSelectAllCheckBox(showCheckBoxInHeader: boolean): void {
    // TODO: uncomment when the select all checkbox in selection mode is removed
    // const selectAllCheckbox = this.elementRef.nativeElement.closest('.modal-content').querySelector('thead th[data-column-id="selectionCheckbox"]');
    // const checkbox = selectAllCheckbox?.querySelector('span.datagrid-checkbox');
    // if (checkbox) {
    //   checkbox.style.display = (showCheckBoxInHeader) ? '' : 'none';
    // }
  }

  private disableComponent() {
    this.enableFirstPage = false;
    this.enablePreviousPage = false;
    this.enableNextPage = false;
    this.enableLastPage = false;
    this.sohoDataGridComponent.data = [];

    if (this.sohoDataGridComponent) {
      this.sohoDataGridComponent.removeSelected();
    }

    this.setEmptyMessage();

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Assumes this.dataViewSet is already set.
   * @param listPagingResponse
   * @return gridPagingResponse
   */
  private postInitialPage(listPagingResponse: LMListPagingResponse, pageOp: string) {

    if (pageOp === 'init') {
      this.updateColumnLabelsOrSortIndicator(this.listSpec, listPagingResponse.dataViewSet, this.getCombinedFilterCriteria());
    }

    this.processListPagingResponse(listPagingResponse, true);

    /**
     * WORKAROND: must do this after processListPagingResponse because
     * datagrid fails if no data is loaded on a null pagingInfo object.
     */
    this.updateSortColumn(this.listSpec); // update the sort indicator

    /**
     * Reset the listResource to be the rangeViewKey + listName once a dataViewSet
     * is initially loaded. This will be the actual listResource used for anything
     * else that happens in the client.
     *
     * This is needed for:
     * - When this list becomes a sourceResource to another list.
     * - Opening a create form, the list will have the range keys.
     *
     *  Phillip
     */
    if (!this.listSpec.isTemplateList) {
      // The dataviewset header corresponds to template list and the paging info contains
      // the required info to paginate over the template list.
      // List resource will be that of targetList and it is used to perform create or update.
      // So do not overwrite the listResource based on dataviewSet header.
      this.listResource = new ListResource({
        resourceId: this.dataViewSet.header.resourceId,
        listName: this.listSpec.busListSpec.name
      });
    }

    this.updateFilterBar();
    this.lmListEventService.dataLoadedEvent(listPagingResponse);
  }

  private updateSortColumn(listSpec: LMListSpec): void {
    // -----------------------------------------------------------------
    // 1. iterate the list display fields
    // -----------------------------------------------------------------
    // 2. filter all but the sort column
    // -----------------------------------------------------------------
    // 3. update the sort indicator
    // -----------------------------------------------------------------
    this.listSpec.busListSpec.displayFields
      .filter(displayField => this.lmListSpecUtilService.isColumnarView(listSpec) && this.dataViewSet.columnarInfo.columnarSortByFieldName
        ? displayField.fieldId === this.dataViewSet.columnarInfo.columnarSortByFieldName
        : displayField.sortOrderName === this.dataViewSet.pagingInfo.sortOrderName)
      .forEach(displayField => {
        const visibleColumns = this.sohoDataGridComponent.getColumns();
        const column: ListGridColumn = this.getFirstVisibleColumnForFieldId(displayField.fieldId, visibleColumns);
        if (column != null) {
          this.sohoDataGridComponent.setSortIndicator(column.id, this.dataViewSet.pagingInfo.isAscending);
        }
      });
  }

  private getFirstVisibleColumnForFieldId(fieldId: string, visibleColumns: Array<ListGridColumn>): ListGridColumn {
    return visibleColumns.find(column => column.fieldId === fieldId);
  }

  private setPagingSubscriptionPageOp(subscription: Subscription, pagingInfo: LMCurrentPagingInfo) {
    subscription['pagingInfo'] = pagingInfo;
  }

  protected _hidePagerBar(): boolean {
    return super._hidePagerBar();
  }

}
